import { fetchReportComments } from '../hooks/comments/fetchReportComments'
import { makeAutoObservable, runInAction } from 'mobx'
import slugify from 'slugify'

class CommentStore {
  /**
   * Define current comment Panel to chartId
   * @type string | undefined
   */
  chartId = undefined
  chartName = '' // TODO: improve this adding chart as relationship (chart: Chart)
  chartFilters = []

  /**
   * Store tempComment: Adding, Editing or Deleting one
   * @type {{}}
   */
  tempComment = {}

  reportComments = []

  chartComments = {}

  /**
   * Turn on/ off showing Panel of comments
   * @type {boolean}
   */
  commentsDrawerVisible = false

  /**
   * Turn visible form to add/edit new comments
   * @type {boolean}
   */
  isFormVisible = false

  /**
   * Loading flags
   * @type {boolean}
   */

  loadingReportComments = false
  loadingChartComments = false

  isEditing = false

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
    // makePersistable('CommentStore', ['chartComments', 'reportComments'])
  }

  setChartId = chartId => {
    this.chartId = chartId
  }

  setChartName = chartName => {
    this.chartName = chartName
  }

  /**
   * Load a list of comments assigned to all charts available in the report
   * @returns {Promise<void>}
   */
  async loadReportComments() {
    runInAction(() => {
      this.loadingReportComments = true
    })
    try {
      this.invalidateComments()
      const reportComments = (await fetchReportComments()) || []

      runInAction(() => {
        this.reportComments = reportComments
      })
    } catch (e) {
      runInAction(() => {
        this.error = 'Error on load data.'
      })
    } finally {
      runInAction(() => {
        this.loadingReportComments = false
      })
    }
  }

  /**
   * Load comments by chartId
   * @param chartId
   * @returns {Promise<void>}
   */
  async loadChartComments() {
    runInAction(() => {
      this.loadingChartComments = true
    })
    try {
      this.invalidateComments()
      const chartComments = (await fetchReportComments(this.chartId)) || {}

      runInAction(() => {
        this.chartComments = chartComments
      })
    } catch (e) {
      runInAction(() => {
        this.error = 'Error on load data.'
      })
    } finally {
      runInAction(() => {
        this.loadingChartComments = false
      })
    }
  }

  /**
   * Invalidate comment to having not mismatch of current ChartId
   * and current comments listed;
   */
  invalidateComments = () => {
    this.chartComments = {}
  }

  /**
   * tempComment Setter
   * @param comment
   */
  setTempComment = comment => {
    this.tempComment = comment
  }

  setIsFormVisible = isVisible => {
    this.isFormVisible = isVisible

    /**
     * Empty any value previous value available such as parentId or coords
     */
    if (!isVisible) {
      this.tempComment = {}
      this.isEditing = false
    }
  }

  setIsEditing = value => {
    this.isEditing = value
  }

  setCommentsDrawerVisible = value => {
    this.commentsDrawerVisible = value
    if (!value) {
      this.chartId = undefined
    }
  }

  turnOnCommentsEnabled = () => {
    this.commentsDrawerVisible = true
  }

  turnOffCommentsEnabled = () => {
    this.chartId = undefined
    this.commentsDrawerVisible = false
  }

  get rootComments() {
    return this.chartComments.comments?.filter(comment => !comment.parentId)
  }

  /**
   * Create a new comment or a new Reply
   * @param text
   * @param user
   * @param parentId
   */
  async createComment(text, user) {
    const created = Date.now()
    const id = slugify(created.toString())
    const newComment = {
      ...this.tempComment, // will get coords if added
      id,
      created,
      updated: created,
      user,
      text,
    }

    if (this.chartComments.comments?.length > 0) {
      this.chartComments.comments.push(newComment)
    } else {
      try {
        if (!this.reportComments) {
          await this.loadReportComments()
        }
        const chartComments = this.reportComments.find(
          chartComments => chartComments.chartId === this.chartId,
        )
        const newChartComment = {
          ...chartComments,
          comments: [newComment],
        }

        this.chartComments = newChartComment
      } catch (error) {
        this.error = `An error happen on create new comments: ${error.getMessage()}`
      }
    }

    // If it has parent Id, add as reply to the parent comment
    if (this.tempComment.parentId) {
      const parentComment = this.chartComments.comments.find(
        comment => comment.id === this.tempComment.parentId,
      )
      if (parentComment) {
        parentComment.replies = [...(parentComment.replies || []), id]
      }
    }

    // Reset tempComment
    this.tempComment = {}
    this.setIsFormVisible(false)
  }

  /**
   * @param {string} text Update text in the comment
   */
  updateComment(text) {
    try {
      let commentIndex = 0
      const commentToUpdate = this.chartComments.comments.find(
        (comment, index) => {
          commentIndex = index
          return comment.id === this.tempComment.id
        },
      )
      commentToUpdate.text = text
      this.chartComments.comments.splice(commentIndex, 1, commentToUpdate)
      this.tempComment = {}
    } catch (e) {
      this.error = 'An error occurred while updating the comment.'
    }
  }

  /**
   * Remove the comment from chartComments
   * and clear the parent replies array for proper organization
   * @param {string} commentId Delete item
   */
  deleteComment(commentId) {
    try {
      /**
       * Delete comment
       */
      let commentIndex
      const comment = this.chartComments.comments.find((comment, index) => {
        commentIndex = index
        return comment.id === commentId
      })

      this.chartComments.comments.splice(commentIndex, 1)

      // Clear parent.replies index
      let parentCommentIndex
      const parentComment = this.chartComments.comments.find(
        (pComment, index) => {
          parentCommentIndex = index
          return pComment.id === comment.parentId
        },
      )

      if (parentComment) {
        parentComment.replies = parentComment.replies.filter(
          id => id !== commentId,
        )
        this.chartComments.comments.splice(parentCommentIndex, 1, parentComment)
      }
    } catch (e) {
      this.error = `An error occurred while updating the comment.: ${commentId}.`
    }
  }
}

export default CommentStore
